import { useState, useEffect, useContext, createContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FamilySessionManager, FamilyAccountManager } from '../models/FamilyAccountTemplate';
import { multiFamilyService } from '../services/multiFamilyService';
import { primaryFamilyCodeFromUserDoc } from '../utils/familyScope';
import logger from '../utils/logger';

// Create context
const FamilySessionContext = createContext(null);

// Provider component
export const FamilySessionProvider = ({ children }) => {
  // Use userData from AuthContext — it was already fetched inside onAuthStateChanged
  // where the Firebase auth token is guaranteed to be attached to Firestore. Re-reading
  // the user document here introduces a race condition where a second Firestore request
  // fires before the Firestore client has attached the auth token, causing transient
  // "Missing or insufficient permissions" errors even for a user's own document.
  const { currentUser, userData } = useAuth();
  const [sessionManager, setSessionManager] = useState(null);
  const [accountManager, setAccountManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeSession = async () => {
      if (!currentUser) {
        setSessionManager(null);
        setAccountManager(null);
        setError(null);
        setLoading(false);
        return;
      }

      // Wait for AuthContext to finish loading userData before proceeding.
      // If userData is still null the auth state is still being fetched —
      // this effect will re-run once userData arrives.
      if (!userData) {
        setLoading(true);
        return;
      }

      const activeFamilyCode = primaryFamilyCodeFromUserDoc(userData);
      if (!activeFamilyCode) {
        setError('No family code found. Complete signup or ask your family admin to re-send your invite.');
        setLoading(false);
        return;
      }

      try {
        // Attempt multi-family migration; never fatal — permission errors from
        // legacy accounts are silently swallowed so the session still initialises.
        try {
          await multiFamilyService.migrateToMultiFamily(currentUser.uid);
        } catch (migrateErr) {
          logger.warn('migrateToMultiFamily skipped (non-fatal):', migrateErr.code || migrateErr.message);
        }

        const newAccountManager = new FamilyAccountManager(activeFamilyCode);
        const userRole = userData.role || 'DEFAULT';
        const newSessionManager = new FamilySessionManager(
          currentUser.uid,
          activeFamilyCode,
          userRole
        );

        setAccountManager(newAccountManager);
        setSessionManager(newSessionManager);
        setError(null);
      } catch (err) {
        logger.error('Error initializing family session:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, [currentUser, userData]);

  const value = {
    sessionManager,
    accountManager,
    loading,
    error,
    getVisibleGroups: () => sessionManager?.getVisibleGroups() || [],
    getManageableGroups: () => sessionManager?.getManageableGroups() || [],
    getActiveGroup: () => sessionManager?.getActiveGroup(),
    setActiveGroup: (groupType) => sessionManager?.setActiveGroup(groupType),
    canPerformAction: (action) => sessionManager?.canPerformAction(action) || false,
    getFeatureAccess: () => sessionManager?.getFeatureAccess() || {},
    getDefaultGroupsForRole: (role) => accountManager?.getDefaultGroupsForRole(role) || [],
    getPermissionsForRole: (role) => accountManager?.getPermissionsForRole(role) || null,
  };

  return (
    <FamilySessionContext.Provider value={value}>
      {children}
    </FamilySessionContext.Provider>
  );
};

export const useFamilySession = () => {
  const context = useContext(FamilySessionContext);
  if (!context) {
    throw new Error('useFamilySession must be used within a FamilySessionProvider');
  }
  return context;
};

export { FAMILY_ROLES } from '../models/FamilyAccountTemplate';
